import React from 'react'
import { Button, Form, useToaster } from 'react-angle-dashboard-components'
import { BottomBarContainer } from '../layouts/styles'
import styled from 'styled-components'
import { useBottomBar } from '../store/selectors'
import { BottomBarButton } from '../store/interfaces'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { useContext } from 'react'
import { GbsStore } from '../store'
import { Actions } from '../store/actions'
import { groupService } from '../services'
import { Contribution } from '../interfaces/contribution'
import { preventNegativeInputs } from '@angle/utils'

const BarButton = styled(Button)`
  margin-left: 20px;

  @media (min-width: 1024px) {
    height: 38px;
    width: 120px;
    font-size: 16px;
    padding: 0;
  }
`

const SelectedPlans = styled.div<{ selected?: boolean }>`
  display: none;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  padding: 28px;
  font-weight: 500;
  ${(props) => (props.selected ? 'background-color: #EFF1F7;' : '')}
  @media (min-width: 715px) {
    display: flex;
  }
`

const SelectedPlansText = styled.p`
  margin: 0;
  padding: 0;
  color: var(--primary);
  font-family: 'Sentinel';
  @media (min-width: 1024px) {
    font-size: 18px;
  }
`

const EditAction = styled.span`
  display: none;
  font-weight: 600;
  color: var(--secondary);
  font-size: 1rem;
  cursor: pointer;
  margin-left: 1rem;

  @media (min-width: 1200px) {
    display: block;
  }
`

const NormalInfoWrapper = styled.div`
  display: none;
  @media (min-width: 1200px) {
    display: flex;
    alingn-items: baseline;
  }
`

const NormalInfo = styled.div`
  border-right: 1px solid #e0e3ec;
  padding: 3px 16px 3px 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
`

const Container = styled.div`
  height: 90px;
  background: #ffffff;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.12);
  border-radius: 7px;
  display: flex;
  align-items: center;
`

export const BottomBar = () => {
  const bottomBarData = useBottomBar()

  const { dispatch } = useContext(GbsStore)

  const { show } = useToaster()

  const { register, setValue, handleSubmit } = useForm({
    defaultValues: {
      avgAge: bottomBarData?.info?.avgAge,
      numberOfEmployees: bottomBarData?.info?.numberOfEmployees,
      zip: bottomBarData?.info?.zip,
      contribution: bottomBarData?.info?.contribution?.value,
      dependentContribution:
        bottomBarData?.info?.contribution?.dependentContribution
    }
  })

  useEffect(() => {
    setValue('avgAge', bottomBarData?.info?.avgAge)
    setValue('zip', bottomBarData?.info?.zip)
    setValue('contribution', bottomBarData?.info?.contribution?.value)
    setValue(
      'dependentContribution',
      bottomBarData?.info?.contribution?.dependentContribution
    )
    setValue('numberOfEmployees', bottomBarData?.info?.numberOfEmployees)
  }, [bottomBarData, setValue])

  const handleSave = handleSubmit(async (data) => {
    const initialQuoteInformation = {
      zip: data.zip,
      numberOfEmployees: data.numberOfEmployees
    }
    const stepsData = {
      recommendedPlans: {
        avgAge: data.avgAge
      },
      contribution: {
        value: Number(data.contribution),
        dependentContribution: Number(data.dependentContribution)
      }
    }

    try {
      await groupService.modify({
        employer_contribution_employee_amount: Number(data.contribution),
        employer_contribution_dependent_amount: Number(
          data.dependentContribution
        ),
        total_individuals: Number(data.numberOfEmployees)
      } as any)

      dispatch({
        type: Actions.SetInitialQuoteInformation,
        payload: initialQuoteInformation
      })

      dispatch({
        type: Actions.SetStepData,
        payload: { stepKey: 'contribution', stepData: stepsData.contribution }
      })

      dispatch({
        type: Actions.SetStepData,
        payload: {
          stepKey: 'recommendedPlans',
          stepData: stepsData.recommendedPlans
        }
      })
    } catch (e) {
      show({
        message: 'Something went wrong',
        icon: 'icon-cancel',
        type: 'danger'
      })
    }
  })

  return (
    <BottomBarContainer hiddenEdit={bottomBarData?.hideEditActions}>
      {bottomBarData && (
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <>
              <SelectedPlans selected={false}>
                <SelectedPlansText>
                  {bottomBarData.info?.selectedPlans?.length || 0} plan
                  {(!bottomBarData.info?.selectedPlans?.length ||
                    bottomBarData.info?.selectedPlans?.length > 1) &&
                    's'}{' '}
                  selected
                </SelectedPlansText>
              </SelectedPlans>
              <NormalInfoWrapper>
                {bottomBarData.info?.avgAge && (
                  <NormalInfo className="text-primary">
                    Avg Age:{' '}
                    {bottomBarData.isEditing &&
                    !bottomBarData.staticEmployeesAndAge ? (
                      <Form.Control
                        style={{ width: 47, height: 30 }}
                        {...register('avgAge')}
                        className="bg-white ml-2 px-2"
                        type="text"
                      />
                    ) : (
                      <span className="text-dark ml-1">
                        {bottomBarData.info?.avgAge}
                      </span>
                    )}
                  </NormalInfo>
                )}
                {bottomBarData.info?.numberOfEmployees ? (
                  <NormalInfo className="text-primary">
                    Emp #:{' '}
                    {bottomBarData.isEditing &&
                    !bottomBarData.staticEmployeesAndAge ? (
                      <Form.Control
                        style={{ width: 47, height: 30 }}
                        className="bg-white ml-2 px-2"
                        type="text"
                        {...register('numberOfEmployees')}
                      />
                    ) : (
                      <span className="text-dark ml-1">
                        {bottomBarData.info?.numberOfEmployees}
                      </span>
                    )}
                  </NormalInfo>
                ) : null}
                {bottomBarData.info?.zip && (
                  <NormalInfo className="text-primary">
                    Zip:{' '}
                    {bottomBarData.isEditing ? (
                      <Form.Control
                        style={{ width: 85, height: 30 }}
                        className="bg-white ml-2 px-2"
                        type="text"
                        {...register('zip')}
                      />
                    ) : (
                      <span className="text-dark ml-1">
                        {bottomBarData.info?.zip}
                      </span>
                    )}
                  </NormalInfo>
                )}
                {bottomBarData.info?.contribution?.type && (
                  <NormalInfo className="text-primary">
                    Employee Contribution:{' '}
                    {bottomBarData.info?.contribution.type ===
                      Contribution.DOLLAR && (
                      <span className="text-dark ml-1">$</span>
                    )}
                    {bottomBarData.isEditing ? (
                      <Form.Control
                        style={{ width: 47, height: 30 }}
                        className="bg-white mx-2 px-2"
                        type="number"
                        {...register('contribution')}
                        onKeyPress={preventNegativeInputs}
                      />
                    ) : (
                      <span className="text-dark ml-1">
                        {bottomBarData.info?.contribution.value}
                      </span>
                    )}
                    {bottomBarData.info?.contribution.type ===
                      Contribution.PERCENTAGE && (
                      <span className="text-dark ml-1">%</span>
                    )}
                  </NormalInfo>
                )}

                {bottomBarData.info?.contribution?.type && (
                  <NormalInfo className="text-primary">
                    Dependent Contribution:{' '}
                    {bottomBarData.info?.contribution.type ===
                      Contribution.DOLLAR && (
                      <span className="text-dark ml-1">$</span>
                    )}
                    {bottomBarData.isEditing ? (
                      <Form.Control
                        style={{ width: 47, height: 30 }}
                        className="bg-white mx-2 px-2"
                        type="number"
                        {...register('dependentContribution')}
                        onKeyPress={preventNegativeInputs}
                      />
                    ) : (
                      <span className="text-dark ml-1">
                        {bottomBarData.info?.contribution.dependentContribution}
                      </span>
                    )}
                    {bottomBarData.info?.contribution.type ===
                      Contribution.PERCENTAGE && (
                      <span className="text-dark ml-1">%</span>
                    )}
                  </NormalInfo>
                )}
              </NormalInfoWrapper>
              {!bottomBarData.hideEditActions && (
                <EditAction
                  onClick={() => {
                    if (bottomBarData.isEditing) handleSave()
                    dispatch({
                      type: Actions.SetBottomBarEdit,
                      payload: !bottomBarData.isEditing
                    })
                  }}
                  className={`fs-14 ${
                    bottomBarData.isEditing ? 'text-primary' : 'text-secondary'
                  }`}
                >
                  {bottomBarData.isEditing ? 'Save' : 'Edit'}
                </EditAction>
              )}
            </>
          </div>
          <div className="mr-5">
            {bottomBarData?.buttons?.map((button: BottomBarButton) => (
              <BarButton
                key={button.text}
                variant={button.variant}
                onClick={button.callback}
                disabled={button.disabled}
              >
                {button.text}
              </BarButton>
            ))}
          </div>
        </Container>
      )}
    </BottomBarContainer>
  )
}

export default BottomBar
